import React from 'react';
import classes from './VersionsTab.module.scss';
import { Avatar, Button, Empty, Form, Input, Table, Tooltip } from 'antd';
import { useAppDispatch, useAppSelector } from 'store/hook';
import { setDocsSegment, setIsOpenNewVersionModal, setIsRSidebarCollapsed } from 'store/slices/windowStateSlice';
import api from 'api';
import { IColumn, IDocument } from 'interfaces';
import TableLoader from 'components/Sceletons/TableLoader';
import { useTranslation } from 'react-i18next';
import { getVersionsColumns } from './columns/versionsColumns';
import dayjs from 'dayjs';
import cn from 'classnames';
import getDateWithTimeZone from 'helpers/getDateWithTimeZone';
import { availableIconFormat, documnentIcons, getCorrectIcon } from 'helpers/documentIcons';
import { DownloadOutlined, SwapOutlined, UserOutlined } from '@ant-design/icons';
import { useSearchParams } from 'react-router';
import { resetViewerSlice } from 'store/slices/viewerSlice';
import ConfirmationModal from 'components/Modals/ConfirmationModal/ConfirmationModal';
import { addFileForDownloading, removeDownloadedFile, setDocuments, setSelectedDocument, setSelectedVersion, setViewType } from 'store/slices/dataDocumentsSlice';
import UploadingComponent from 'components/UploadingComponent/UploadingComponent';
import { getFile } from 'helpers/downloadSomeFile';
import { AxiosResponse } from 'axios';
import { typesToSigning } from 'helpers/filesExtensions';
import { setChatData } from 'store/slices/chatsSlice';

type VersionsTabProps = {
  versionsData: IDocument[];
  isLoadingVersions: boolean;
}

const VersionsTab = ({versionsData, isLoadingVersions}: VersionsTabProps) => {
  const [modifiedColumns, setModifiedColumns] = React.useState<IColumn[]>([]);
  const [choosenVersion, setChoosenVersion] = React.useState<IDocument | null>(null);
  const [isDownloading, setIsDownloading] = React.useState(false);
  const [form] = Form.useForm();
  
  const [isOpenRollbackModal, setIsOpenRollbackModal] = React.useState(false);
  const [isRollbacking, setIsRollbacking] = React.useState(false);

  const [isOpenRestoreModal, setIsOpenRestoreModal] = React.useState(false);
  const [isRestoring, setIsRestoring,] = React.useState(false);

  const { dataRoom } = useAppSelector((state) => state.dataRoom);
  const { selectedDocument, documents, folders, currentFolder, selectedVersion } = useAppSelector((state) => state.documents);
  const { userData } = useAppSelector((state) => state.userData);
  const { userFoldersPermissions, userRootPermissions } = useAppSelector((store) => store.permissions);
  const { messageApi } = useAppSelector((state) => state.windowState);
  const { roomAllChats, chatData } = useAppSelector((state) => state.chat);

  const [searchParams] = useSearchParams();
  const docInViewer = searchParams.get('document_id');

  const typeDocument = availableIconFormat.includes(selectedDocument?.extension || '') ? selectedDocument?.extension : 'default';
  const iconDocument = typeDocument as keyof typeof documnentIcons;
  
  const timeOffset = dayjs().utcOffset();

  const isRootFolder = !currentFolder[0];
  const targetFolderPermissions = (isRootFolder && userRootPermissions)
    ? userRootPermissions.permissions
    : userFoldersPermissions?.find(folder => folder.id === currentFolder[0]?.id)?.permissions;
      
  const isAvailableUploadNewVersion = targetFolderPermissions?.includes('can_upload');

  const dispatch = useAppDispatch();
  const { t, i18n } = useTranslation();

  const openSupportingModal = (version: IDocument, openHandle: () => void) => {
    setChoosenVersion(version);
    openHandle();
  };

  const closeSupportingModals = () => {
    setIsOpenRestoreModal(false);
    setIsOpenRollbackModal(false);
    setChoosenVersion(null);
  };

  const downloadVersion = async (version: IDocument) => {
    setChoosenVersion(version);
    setIsDownloading(true);

    const downloadPayload = {
      extension: version.extension,
      token: version.token,
      id: dataRoom?.id,
      entity: version.id,
      action: version.is_confidential ? 'download_confident' : 'download',
      is_agreement: false,
      is_webview: false,
      signing_status: version.signing_status,
      locale: i18n.language
    };

    
    const downloadInPDF = !selectedDocument?.permissions?.includes('can_download') && selectedDocument?.permissions?.includes('can_download_pdf');
    const iconOfDownloadingVersion = downloadInPDF ? 'pdf' : version?.extension || '';
    const correctFileName = downloadInPDF ? version.name.replace(/\.\w+$/, '.pdf') : version.name
    dispatch(addFileForDownloading({...downloadPayload, name: correctFileName}));
    try {
      let file: AxiosResponse<any, any>
      if (downloadInPDF) {
        const response = await api.convertRequest(downloadPayload);     
         if (response.data.status === 'ready') {
          const requestPayload = JSON.parse(String(response.config.data));
          const converted = await api.getConvertedFile(requestPayload);
          file = converted;
        } else { 
          return;
        }
      } else {
        file = await api.downloadFile(downloadPayload);
      }

      getFile(file.data as Blob, correctFileName);
      dispatch(removeDownloadedFile(version?.token));

      messageApi?.success(<div>
        <span>{documnentIcons[getCorrectIcon(iconOfDownloadingVersion)]}</span> {correctFileName} - {t('Documents.details.versions.successDownload')}
      </div>, 5);
    } catch {
      messageApi?.error(t('Documents.details.versions.errorDownload'));
      dispatch(removeDownloadedFile(version?.token));
    } finally {
      setIsDownloading(false);
      setChoosenVersion(null);
    }
  }

  const onRestore = async () => {
    setIsRestoring(true);
    try {
      const responseVersionRestore = await api.versionRestore({version_id: choosenVersion?.id!});
      messageApi?.success(<div>
        <span>{documnentIcons[getCorrectIcon(choosenVersion?.extension || '')]}</span> {choosenVersion?.name} - {t('Documents.details.versions.rollbackSuccess')}
      </div>, 5);
    
      dispatch(setDocuments({
        documents: [
          ...documents.map(document => ({
            ...document,
            message_amount: document.message_amount,
            unviewed_amount: document.unviewed_amount,
          })),
          {
            ...choosenVersion!,
            id: responseVersionRestore.data?.id,
            status: 0,
            upload_at: dayjs().valueOf() - timeOffset * 60 * 1000,
          }
      ],
        folders
      }));
    } catch (e) {
      messageApi?.error(t('Documents.details.versions.restoreError'));
    } finally {
      setIsRestoring(false);
      closeSupportingModals();
    }
  };

  const onRollback = async () => {
    setIsRollbacking(true);
    try {
      const responseVersionReset = await api.versionReset({ version_id: choosenVersion?.id!, comment: form.getFieldValue('comment') });      
      messageApi?.success(<div>
        <span>{documnentIcons[getCorrectIcon(choosenVersion?.extension || '')]}</span> {choosenVersion?.name} - {t('Documents.details.versions.restoreSuccess')}
      </div>, 5);

      dispatch(setDocuments({
        documents: documents.map(document => {
          return document.id === selectedDocument!.id
            ? {
                ...document,
                name: responseVersionReset.data.name,
                id: responseVersionReset.data.id,
                extension: responseVersionReset.data.extension,
                token: responseVersionReset.data.token,
                message_amount: document.message_amount,
                unviewed_amount: document.unviewed_amount,
              }
            : document
        }),
        folders
      }));
      const targetChat = roomAllChats.find((chat) => chat.entity_id === selectedDocument!.id);
      if (targetChat && chatData[targetChat.id]) {
        dispatch(setChatData({
          ...chatData,
          [targetChat.id]: {
            ...chatData[targetChat.id],
            data: [],
            grouped: [],
            isLoadedHistory: false,
          },
        }));
      }

      dispatch(setViewType('Table'));
      dispatch(setSelectedDocument(null));
      dispatch(resetViewerSlice());
      form.resetFields(['comment'])
    } catch (e) {
      messageApi?.error(t('Documents.details.versions.rollbackError'));
    } finally {
      setIsRollbacking(false);
      closeSupportingModals();
    }
  };

  React.useEffect(()=> {
    const documentColumns = getVersionsColumns(t);
    const customColumns = documentColumns?.map((col: IColumn) => ({
      ...col,
      render: (value: any, info: any) => renderCell({value, info, col}),
    }))

    setModifiedColumns(customColumns);
  },[versionsData, i18n.language, docInViewer, selectedVersion?.id]);  

  const renderCell = (props: any) => {
    const { value, info, col } = props;
    const icon = availableIconFormat.includes(info.extension) ? info.extension : 'default';
    
    const currentActive = selectedVersion?.id || docInViewer;
    const activeDoc = currentActive && currentActive === info?.id;

    if (col.key === 'name') {
      return <div className={cn(classes.rowWrap, activeDoc ? classes.active : 'null')}>
        <div className={classes.icon}>
          <Avatar
            className={classes.avatar}
            icon={documnentIcons[icon as keyof typeof documnentIcons]}
          />
        </div>
        {value}
      </div>
    }

    if(col.key === 'upload_at'){
      return value && getDateWithTimeZone(value, userData?.date_format!);
    }

    if (col.key === 'author') {
      return value && <div>
         {<UserOutlined className={classes.authorIcon}/>}
         <span style={{paddingLeft: 4}}>{value}</span>
      </div>
    }

    if (col.key === 'version_comment') {
      if (value && value.includes('Editing in system by')) {
        const editingAuthor = value && value.replace('Editing in system by ','');
        const localizedEditingComment = t('Documents.details.versions.editsBy', { author: editingAuthor });
        return <div className={classes.commentDiv}>
          {localizedEditingComment}
        </div>
      }
      
      return <div className={classes.commentDiv}>
        {value || '-'}
      </div>
    }

    if (col.key === 'details') {
      if (selectedDocument?.id === info.id){
        return <div className={classes.current}>{t('Documents.details.versions.current')}</div>
      }

      const isAvailableDownloadVersion = [...typesToSigning, 'xls', 'xlsx'].includes(info.extension)
        ? selectedDocument?.permissions?.includes('can_download') || selectedDocument?.permissions?.includes('can_download_pdf')
        : selectedDocument?.permissions?.includes('can_download');

      return (
        <div className={classes.details}>
          <Tooltip placement='topRight' title={t('Documents.details.versions.download')}>
            <Button
              className={isAvailableDownloadVersion ? classes.iconButton : 'null'}
              disabled={!isAvailableDownloadVersion || Boolean(choosenVersion?.id)}
              size='small'
              type='text'
              loading={choosenVersion?.id === info.id && isDownloading}
              icon={<DownloadOutlined style={{fontSize: 18}}/>}
              onClick={() => downloadVersion(info)}
            />
          </Tooltip>

          {/* <Tooltip placement='topRight' title={t('Documents.details.versions.restore')}>
            <Button
              className={isAvailableUploadNewVersion ? classes.iconButton : 'null'}
              disabled={!isAvailableUploadNewVersion}
              size='small'
              type='text'
              icon={<RollbackOutlined style={{fontSize: 18}}/>}
              onClick={() => openSupportingModal(info, () => setIsOpenRestoreModal(true))}
            />
          </Tooltip> */}

          <Tooltip placement='topRight' title={t('Documents.details.versions.rollback')}>
            <Button
              className={isAvailableUploadNewVersion ? classes.iconButton : 'null'}
              disabled={!isAvailableUploadNewVersion}
              size='small'
              type='text'
              icon={<SwapOutlined style={{fontSize: 18}}/>}
              onClick={() => openSupportingModal(info, () => setIsOpenRollbackModal(true))}
            />
          </Tooltip>
        </div>
      )
    }
    
    return value;
  };

  const rollbackText = (
    <div>
      <div className={classes.confirmText}>{t('Documents.details.versions.rollbackDocument')}</div>
      <div className={classes.confirmDocCard}>
        <span style={{paddingRight: 3}}>{documnentIcons[iconDocument]}</span>
        {selectedDocument?.name} ( {t('Documents.details.versions.tableColumns.date')} - {getDateWithTimeZone(selectedDocument?.upload_at, userData?.date_format!)} )
      </div>
      <div className={classes.confirmText}>{t('Documents.details.versions.toVersion')}</div>
      <div className={classes.confirmDocCard}>
        <span style={{paddingRight: 3}}>{documnentIcons[getCorrectIcon(choosenVersion?.extension || '')]}</span>
        {choosenVersion?.name} ( {t('Documents.details.versions.tableColumns.date')} - {getDateWithTimeZone(choosenVersion?.upload_at, userData?.date_format!)} )
      </div>
      <Form form={form} layout='vertical' style={{paddingTop: 10}}>
        <Form.Item
          label={<span style={{fontWeight: 600}}>{t('Documents.modals.newVersion.commentReset')}</span>}
          name='comment'
        >
          <Input.TextArea
            maxLength={500}
            showCount
            placeholder={t('Documents.modals.newVersion.enterCommentReset')}
            autoSize={{ maxRows: 10, minRows: 4 }}
          />
        </Form.Item>
      </Form>
    </div>
  );

  const restoreText = (
    <div>
      <div className={classes.confirmText}>{t('Documents.details.versions.restoreDocument')}</div>
      <div className={classes.confirmDocCard}>
        <span style={{paddingRight: 3}}>{documnentIcons[getCorrectIcon(choosenVersion?.extension || '')]}</span>
        {choosenVersion?.name} ( {t('Documents.details.versions.tableColumns.date')} - {getDateWithTimeZone(choosenVersion?.upload_at, userData?.date_format!)} )
      </div>
      <div className={classes.confirmSubText}>{t('Documents.details.versions.restoreExplanation')}</div>
    </div>
  );
  
  return (
    <div className={classes.versionsWrap}>
      <div className={classes.versionsTable}>
        { isLoadingVersions 
          ? <TableLoader height={'calc(100dvh - 144px)'} padding={5} borderradius={0} margintop={5}/>
          : <Table
            locale={{emptyText: 
              <Empty
                description={t('Documents.details.versions.noVersions')}
                image={Empty.PRESENTED_IMAGE_SIMPLE}
              />
            }}
            className={classes.table}
            dataSource={versionsData}
            columns={modifiedColumns}
            size='small'
            pagination={false}
            scroll={{ y: 'calc(90vh - 144px)' }}
            rowClassName={classes.hoverable}
            onRow={(record, idx: any) => {
              const onViewClick = async (event: any) => {
                const targetDoc = record?.id === docInViewer ? null : record
                dispatch(setSelectedVersion(targetDoc as IDocument));
                dispatch(setIsRSidebarCollapsed({ collapsed: true, entity: '', width: 0 }));
                record?.id !== docInViewer && dispatch(setDocsSegment('view'));
              }
    
              return {
                onDoubleClick: onViewClick,
              }
            }}
          />
        }
      </div>
      <div className={classes.newVersion}>
        <Tooltip title={Boolean(selectedDocument?.signing_status !== null) ? t('Documents.details.versions.signedDocument') : null}>
          <Button
            onClick={() => dispatch(setIsOpenNewVersionModal(true))}
            type='primary'
            disabled={!isAvailableUploadNewVersion || Boolean(selectedDocument?.signing_status !== null)}
          >
            {t('Documents.details.versions.uploadVersion')}
          </Button>
        </Tooltip>
      </div>

      {isOpenRollbackModal && (
        <React.Suspense fallback={<div />}>
          <ConfirmationModal
            isOpen={isOpenRollbackModal}
            onCancel={closeSupportingModals}
            isLoading={isRollbacking}
            submit={onRollback}
            text={rollbackText}
            title={t('Documents.details.versions.rollbackVersionTitle')}
            okText={t('Documents.details.versions.rollback')}
            customClassname={'restore'}
          />
        </React.Suspense>
      )}

      {isOpenRestoreModal && (
        <React.Suspense fallback={<div />}>
          <ConfirmationModal
            isOpen={isOpenRestoreModal}
            onCancel={closeSupportingModals}
            isLoading={isRestoring}
            submit={onRestore}
            text={restoreText}
            title={t('Documents.details.versions.restoreVersionTitle')}
            okText={t('Documents.details.versions.restore')}
          />
        </React.Suspense>
      )}

      {(isRestoring || isRollbacking) && <UploadingComponent />}

    </div>
  );
}

export default VersionsTab;
